// Copyright ©️ 2026 eVolve MEP, LLC

import type { ConduitSize } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import type { BendCreatorFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import type { LastValidationState } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

/** Until a bender's real radius is known from validation, bends are drawn and estimated at this multiple of the conduit diameter. */
const STAND_IN_RADIUS_DIAMETERS = 6;

/** The stand-in bend radius for a conduit diameter. Both are in decimal feet. */
export const getStandInBendRadius = (conduitDiameter: number) => conduitDiameter * STAND_IN_RADIUS_DIAMETERS;

/**
 * The bend radius from the last validation, in decimal feet, when it was for the same bend type, conduit size and
 * bender as `values`. Otherwise null.
 */
export const getValidatedBendRadius = (
  lastValidation: LastValidationState | null,
  values: Pick<BendCreatorFormValues, 'bendType' | 'conduitSizeId' | 'benderProfile'>,
) => {
  if (isNil(lastValidation) || !lastValidation.isSuccess) return null;
  const { request, response } = lastValidation;
  return request.bendType === values.bendType &&
    request.conduitSizeId === values.conduitSizeId &&
    request.benderProfileId === values.benderProfile?.machineProfileId
    ? response.bendRadius
    : null;
};

/**
 * The best bend radius known for `values`, in decimal feet: the validated radius when there is one, or else the
 * stand-in for the chosen conduit size. Zero before a conduit size is chosen.
 */
export const getBestKnownBendRadius = (
  lastValidation: LastValidationState | null,
  values: Pick<BendCreatorFormValues, 'bendType' | 'conduitSizeId' | 'benderProfile'>,
  conduitSizes: ConduitSize[] | undefined,
) =>
  getValidatedBendRadius(lastValidation, values) ??
  getStandInBendRadius(
    conduitSizes?.find(({ conduitSizeId }) => conduitSizeId === values.conduitSizeId)?.diameter ?? 0,
  );
